import { AnnualTimelineRow, DebtAllocationSnapshot, FinancialInputs, HistoricalActualYear, ModelResult, PropertyModelEvent, PropertyRiskProfile, SavedModelScenario, StressTestResult } from '../types';
import { buildMonthlyProjection } from './buildMonthlyProjection';
import { calculateMonthlyPayment } from './buildAmortizationSchedule';
import { rollupMonthlyToAnnual } from './rollupMonthlyToAnnual';
import { buildLegacyDebtSnapshot, derivePropertyHoldPeriodYears } from './debt';
import { getScenarioModelEvents } from './scenarios';

const MIN_IRR_RATE = -0.9999;
const MAX_IRR_RATE = 100;
const IRR_PRECISION = 0.00001;
const IRR_MAX_ITERATIONS = 100;

const createEmptyRiskProfile = (): PropertyRiskProfile => ({
  majorSystems: {
    roof: { key: 'roof', label: 'Roof', age: 0, expectedLife: 25, replacementCost: 0, condition: 'good', priority: 'low', notes: '' },
    hvac: { key: 'hvac', label: 'HVAC', age: 0, expectedLife: 15, replacementCost: 0, condition: 'good', priority: 'low', notes: '' },
    waterHeater: { key: 'waterHeater', label: 'Water Heater', age: 0, expectedLife: 10, replacementCost: 0, condition: 'good', priority: 'low', notes: '' }
  },
  structural: {
    plumbing: { key: 'plumbing', label: 'Plumbing', condition: 'sound', estimatedCost: 0, notes: '' },
    electrical: { key: 'electrical', label: 'Electrical', condition: 'sound', estimatedCost: 0, notes: '' },
    foundation: { key: 'foundation', label: 'Foundation', condition: 'sound', estimatedCost: 0, notes: '' },
    windows: { key: 'windows', label: 'Windows', condition: 'sound', estimatedCost: 0, notes: '' },
    flooring: { key: 'flooring', label: 'Flooring', condition: 'sound', estimatedCost: 0, notes: '' },
    exterior: { key: 'exterior', label: 'Exterior', condition: 'sound', estimatedCost: 0, notes: '' }
  },
  minorItems: [],
  deferredMaintenance: [],
  scenario: {
    vacancyMonths: { value: 0, appliesInYear: 1, notes: '' },
    turnoverCost: { value: 0, appliesInYear: 1, notes: '' },
    handymanCost: { value: 0, appliesInYear: 1, notes: '' },
    makeReadyCost: { value: 0, appliesInYear: 1, notes: '' }
  },
  eventSchedule: [],
  notes: ''
});

export const calculateTotalInitialInvestment = (inputs: FinancialInputs, financedAmount: number = Math.max(0, (inputs.purchasePrice || 0) - (inputs.downPayment || 0))): number => (
  // Investor cash in should include equity plus non-financed upfront costs and any
  // reserve capital the model intentionally treats as funded on day 0.
  Math.max(0, (inputs.purchasePrice || 0) - financedAmount) +
  (inputs.acquisitionCosts ?? 0) +
  (inputs.lenderFees ?? 0) +
  (inputs.initialCapex ?? 0) +
  (inputs.propertyReserves ?? 0) +
  (inputs.portfolioReservesAllocated ?? 0)
);

export const calculateReserveCapital = (inputs: FinancialInputs): number => (
  (inputs.propertyReserves ?? 0) + (inputs.portfolioReservesAllocated ?? 0)
);

export const buildTotalInitialInvestmentBreakdown = (
  inputs: FinancialInputs,
  financedAmount: number = Math.max(0, (inputs.purchasePrice || 0) - (inputs.downPayment || 0))
) => ({
  financedAmount,
  equityContribution: Math.max(0, (inputs.purchasePrice || 0) - financedAmount),
  acquisitionCosts: inputs.acquisitionCosts ?? 0,
  lenderFees: inputs.lenderFees ?? 0,
  initialCapex: inputs.initialCapex ?? 0,
  propertyReserves: inputs.propertyReserves ?? 0,
  portfolioReservesAllocated: inputs.portfolioReservesAllocated ?? 0
});

const buildActualTimelineRow = (
  actual: HistoricalActualYear,
  historicalDebtServiceByYear: Record<number, number> = {}
): AnnualTimelineRow => {
  const expenses = (actual.annualTaxes || 0) + (actual.annualInsurance || 0) + (actual.annualHoa || 0) + (actual.annualMaintenance || 0) + (actual.utilities || 0);
  const noi = (actual.annualRent || 0) - expenses;
  const debtService = historicalDebtServiceByYear[actual.year] ?? null;
  const operatingCashFlow = debtService === null ? null : noi - debtService;
  return {
    id: actual.id,
    calendarYear: actual.year,
    rowType: 'actual',
    displayLabel: 'Actual',
    notes: actual.notes || '',
    grossRent: actual.annualRent || 0,
    egi: actual.annualRent || 0,
    expenses,
    noi,
    interestPaid: null,
    principalPaid: null,
    debtService,
    operatingCashFlow,
    loanBalance: null,
    cumulativePrincipalPaid: null,
    assumedSalePrice: null,
    netSaleProceeds: null,
    reserveRelease: null,
    coc: null,
    irr: null,
    moic: null
  };
};

const buildForecastTimelineRow = (year: any, calendarYear: number, rowType: 'current' | 'forecast'): AnnualTimelineRow => ({
  id: `${rowType}-${calendarYear}`,
  calendarYear,
  rowType,
  displayLabel: rowType === 'current' ? 'Current / Base' : 'Forecast',
  notes: '',
  grossRent: year.grossRent,
  egi: year.egi,
  expenses: year.expenses,
  noi: year.noi,
  interestPaid: year.interestPaid,
  principalPaid: year.principalPaid,
  debtService: year.debtService,
  operatingCashFlow: year.operatingCf,
  loanBalance: year.loanBalance,
  cumulativePrincipalPaid: year.cumulativePrincipalPaid,
  assumedSalePrice: year.assumedSalePrice,
  netSaleProceeds: year.netSaleProceeds,
  reserveRelease: year.reserveRelease,
  coc: year.coc,
  irr: year.irr,
  moic: year.moic
});

const calculateNpv = (cashFlows: number[], rate: number): number => {
  if (rate <= MIN_IRR_RATE) return Number.POSITIVE_INFINITY;
  let npv = 0;
  for (let t = 0; t < cashFlows.length; t++) npv += cashFlows[t] / Math.pow(1 + rate, t);
  return npv;
};

const calculateNpvDerivative = (cashFlows: number[], rate: number): number => {
  if (rate <= MIN_IRR_RATE) return Number.NEGATIVE_INFINITY;
  let dNpv = 0;
  for (let t = 1; t < cashFlows.length; t++) dNpv -= t * cashFlows[t] / Math.pow(1 + rate, t + 1);
  return dNpv;
};

const hasCashFlowSignChange = (cashFlows: number[]): boolean => {
  const hasPositive = cashFlows.some((v) => v > 0);
  const hasNegative = cashFlows.some((v) => v < 0);
  return hasPositive && hasNegative;
};

export const calculateIRR = (cashFlows: number[], guess: number = 0.1): number | null => {
  if (!hasCashFlowSignChange(cashFlows)) return null;

  let rate = Math.min(MAX_IRR_RATE, Math.max(MIN_IRR_RATE + 0.0001, guess));
  for (let i = 0; i < IRR_MAX_ITERATIONS; i++) {
    const npv = calculateNpv(cashFlows, rate);
    const dNpv = calculateNpvDerivative(cashFlows, rate);
    if (!Number.isFinite(npv) || !Number.isFinite(dNpv) || Math.abs(dNpv) < 1e-10) break;
    const nextRate = rate - npv / dNpv;
    if (!Number.isFinite(nextRate) || nextRate <= MIN_IRR_RATE || nextRate > MAX_IRR_RATE) break;
    if (Math.abs(nextRate - rate) < IRR_PRECISION) return nextRate;
    rate = nextRate;
  }

  let low = MIN_IRR_RATE;
  let high = Math.max(0.1, guess, 0.2);
  let npvLow = calculateNpv(cashFlows, low);
  let npvHigh = calculateNpv(cashFlows, high);
    while (npvLow * npvHigh > 0 && high < MAX_IRR_RATE) {
    high = Math.min(MAX_IRR_RATE, (high * 2) + 0.1);
    npvHigh = calculateNpv(cashFlows, high);
  }
  if (npvLow * npvHigh > 0) return null;

  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    const npvMid = calculateNpv(cashFlows, mid);
    if (!Number.isFinite(npvMid)) {
      low = mid;
      continue;
    }
    if (Math.abs(npvMid) < IRR_PRECISION || Math.abs(high - low) < IRR_PRECISION) return mid;
    if (npvLow * npvMid <= 0) {
      high = mid;
      npvHigh = npvMid;
    } else {
      low = mid;
      npvLow = npvMid;
    }
  }
  return (low + high) / 2;
};

export const buildHoldPeriodCashFlowSeries = (
  totalInitialInvestment: number,
  annualCashFlows: Array<{ operatingCf: number; totalCashFlow: number }>,
  terminalYearIndex: number
): number[] => {
  if (annualCashFlows.length === 0) return [-totalInitialInvestment];
  const safeTerminalYearIndex = Math.max(0, Math.min(terminalYearIndex, annualCashFlows.length - 1));

  return [
    -totalInitialInvestment,
    ...annualCashFlows.slice(0, safeTerminalYearIndex).map((year) => year.operatingCf),
    annualCashFlows[safeTerminalYearIndex]?.totalCashFlow || 0
  ];
};

export const buildModel = (
  inputs: FinancialInputs,
  riskProfile?: PropertyRiskProfile,
  linkedDebtSnapshot?: DebtAllocationSnapshot | null,
  timelineConfig?: { baseYear?: number; historicalActuals?: HistoricalActualYear[] },
  overlayConfig?: { modelEvents?: PropertyModelEvent[]; scenario?: SavedModelScenario | null }
): ModelResult => {
  const activeRiskProfile = riskProfile || createEmptyRiskProfile();
  const baseYear = timelineConfig?.baseYear || new Date().getFullYear();
  const historicalActuals = (timelineConfig?.historicalActuals || []).filter((entry) => entry.year < baseYear);
  const modelEvents = overlayConfig?.modelEvents || [];
  const scenarioEvents = getScenarioModelEvents(overlayConfig?.scenario || null);
  const debtSnapshot = linkedDebtSnapshot ?? buildLegacyDebtSnapshot(inputs);
  const historicalDebtServiceByYear = debtSnapshot?.annualDebtServiceByYear || {};
  // Shared/portfolio debt can be analytically allocated above a property's
  // purchase price, especially after later recapitalizations. We cap the
  // acquisition financing basis at purchase price so investor basis and
  // return metrics do not collapse to zero or go nonsensical.
  const financedAmount = Math.min(
    Math.max(0, inputs.purchasePrice || 0),
    Math.max(0, debtSnapshot?.allocatedOriginalLoanAmount || 0)
  );
  const totalInitialInvestmentBreakdown = buildTotalInitialInvestmentBreakdown(inputs, financedAmount);
  const totalInitialInvestment = calculateTotalInitialInvestment(inputs, financedAmount);
  const reserveCapital = calculateReserveCapital(inputs);
  const includesReserveReleaseOnSale = inputs.includeReserveReleaseOnSale ?? false;
  const loanAmount = financedAmount;
  const monthlyPayment = debtSnapshot?.monthlyPayment || calculateMonthlyPayment(loanAmount, inputs.interestRate, inputs.amortYears);
  const monthlyProjection = buildMonthlyProjection(inputs, activeRiskProfile, debtSnapshot, [...modelEvents, ...scenarioEvents]);
  const proForma = rollupMonthlyToAnnual(monthlyProjection);
  const derivedHoldYears = derivePropertyHoldPeriodYears(debtSnapshot);
  const holdMonthIndex = Math.max(0, Math.min(monthlyProjection.length - 1, derivedHoldYears * 12 - 1));
  const holdYearIndex = Math.floor(holdMonthIndex / 12);
  const getYearExitValues = (yearIndex: number) => {
    const yearEndMonthIndex = Math.min(monthlyProjection.length - 1, ((yearIndex + 1) * 12) - 1);
    const yearEndMonth = monthlyProjection[yearEndMonthIndex];
    // Current value is appreciation-driven; the implied exit cap becomes NOI / current value.
    const assumedSalePrice = inputs.purchasePrice * Math.pow(1 + (inputs.appreciationRate / 100), yearIndex + 1);
    const loanBalance = yearEndMonth?.endingLoanBalance || 0;
    const costOfSale = assumedSalePrice * (inputs.saleCosts / 100);
    const netSaleProceeds = assumedSalePrice - costOfSale - loanBalance;
    // Reserve release is included once in the terminal event because reserve balances are
    // funded over time but are not otherwise distributed back to the investor in operating CF.
    const endingReserveBalance = yearEndMonth?.endingReserveBalance ?? 0;
    const reserveRelease = includesReserveReleaseOnSale ? endingReserveBalance : 0;

    return {
      yearEndMonth,
      loanBalance,
      assumedSalePrice,
      netSaleProceeds,
      endingReserveBalance,
      reserveRelease,
      totalCashFlow: (proForma[yearIndex]?.operatingCf || 0) + netSaleProceeds + reserveRelease
    };
  };

  proForma.forEach((year, yearIndex) => {
    year.calendarYear = baseYear + yearIndex;
    year.rowType = yearIndex === 0 ? 'current' : 'forecast';
    const exitValues = getYearExitValues(yearIndex);
    year.loanBalance = exitValues.loanBalance;
    year.assumedSalePrice = exitValues.assumedSalePrice;
    year.netSaleProceeds = exitValues.netSaleProceeds;
    year.reserveRelease = exitValues.reserveRelease;
    year.equityFromSale = exitValues.netSaleProceeds;
    year.totalCashFlow = exitValues.totalCashFlow;

    // Row-level IRR means: "What is the hold-through-end-of-this-year IRR if I sell here?"
    const yearCashFlows = buildHoldPeriodCashFlowSeries(totalInitialInvestment, proForma, yearIndex);
    const exitIrr = calculateIRR(yearCashFlows, 0.1);
    const exitMoicCashReturned = proForma
      .slice(0, yearIndex)
      .reduce((sum, row) => sum + row.operatingCf, 0) + year.totalCashFlow;

    year.coc = (year.operatingCf / Math.max(1, totalInitialInvestment)) * 100;
    year.moic = exitMoicCashReturned / Math.max(1, totalInitialInvestment);
    year.irr = exitIrr === null ? null : exitIrr * 100;
  });

  const selectedHoldYear = proForma[holdYearIndex];
  const finalMonth = monthlyProjection[holdMonthIndex];
  const cumulativePrincipalPaid = finalMonth?.cumulativePrincipalPaid || 0;
  const projectedNetProceeds = selectedHoldYear?.netSaleProceeds || 0;

  // Summary IRR is the canonical user-selected hold-period IRR:
  // initial cash in at time 0, interim operating CF in prior full years,
  // and terminal-year operating CF + sale proceeds + reserve release in the hold year.
  const irrCashFlowSeries = buildHoldPeriodCashFlowSeries(totalInitialInvestment, proForma, holdYearIndex);
  const irrDecimal = calculateIRR(irrCashFlowSeries, 0.1);
  const irr = irrDecimal === null ? null : irrDecimal * 100;
  const moic = (
    proForma.slice(0, holdYearIndex).reduce((sum, year) => sum + year.operatingCf, 0) +
    (selectedHoldYear?.totalCashFlow || 0)
  ) / Math.max(1, totalInitialInvestment);

  const year1Months = monthlyProjection.slice(0, 12);
  const month1 = year1Months[0];
  const year1CoC = (year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) / Math.max(1, totalInitialInvestment)) * 100;
  const year1InterestPaid = year1Months.reduce((sum, row) => sum + row.interestPaid, 0);
  const year1PrincipalPaid = year1Months.reduce((sum, row) => sum + row.principalPaid, 0);
  const monthlyNoi = month1?.monthlyNoi || 0;
  const monthlyCashFlow = month1?.endingCashFlow || 0;
  const monthlyPrincipalPaydown = month1?.principalPaid || 0;
  const monthlyAppreciation = month1?.monthlyAppreciation || 0;
  const monthlyTrueReturn = month1?.monthlyTrueReturn || 0;
  const annualCashFlow = year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0);
  const annualPrincipalPaydown = year1PrincipalPaid;
  const annualAppreciation = year1Months.reduce((sum, row) => sum + row.monthlyAppreciation, 0);
  const annualTrueReturn = year1Months.reduce((sum, row) => sum + row.monthlyTrueReturn, 0);
  const annualNoi = year1Months.reduce((sum, row) => sum + row.monthlyNoi, 0);
  const annualOperatingCashFlowBeforeReserves = year1Months.reduce((sum, row) => sum + row.operatingCashFlowBeforeReserves, 0);
  const annualRepairReserveFunding = year1Months.reduce((sum, row) => sum + row.repairReserve, 0);
  const annualTurnoverReserveFunding = year1Months.reduce((sum, row) => sum + row.turnoverReserve, 0);
  const annualReserveFunding = annualRepairReserveFunding + annualTurnoverReserveFunding;
  const annualOperatingExpenses = year1Months.reduce((sum, row) => sum + row.propertyTaxes + row.insurance + row.hoa + row.propertyManagement + row.routineMaintenance + row.utilities, 0);
  const effectiveGrossIncome = year1Months.reduce((sum, row) => sum + row.effectiveRentalIncome + row.otherIncome, 0);
  const annualizedCapEx = year1Months.reduce((sum, row) => sum + row.repairReserve + row.turnoverReserve + row.capexEvents + row.makeReadyCosts + row.otherEventExpense, 0);
  const riskAdjustedAnnualCashFlow = annualCashFlow - annualizedCapEx;
  const largestLikelyExpense = Math.max(
    ...activeRiskProfile.minorItems.map((item) => item.estimatedCost),
    ...activeRiskProfile.deferredMaintenance.map((item) => item.estimatedCost),
    ...Object.values(activeRiskProfile.majorSystems).map((item) => item.replacementCost),
    5000
  );
  const averageMonthlyFixedOutflow = year1Months.reduce((sum, row) => sum + row.propertyTaxes + row.insurance + row.hoa + row.propertyManagement + row.routineMaintenance + row.utilities + row.totalDebtService, 0) / Math.max(1, year1Months.length);
  const monthsOfExpenseCoverage = averageMonthlyFixedOutflow > 0 ? reserveCapital / averageMonthlyFixedOutflow : 999;
  const year1VacancyMonths = Math.round((inputs.vacancyRate || 0) / 100 * 12);

  const stressTests: StressTestResult[] = [
    { scenarioName: 'One-time Expense ($5k)', adjustedAnnualCashFlow: year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) - 5000, adjustedMonthlyCashFlow: (year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) - 5000) / 12, cocAfterEvent: ((year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) - 5000) / Math.max(1, totalInitialInvestment)) * 100, remainingReserves: reserveCapital - 5000, monthsToRecover: 5000 / Math.max(1, year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) / 12), reserveCoverageRatio: reserveCapital / 5000 },
    { scenarioName: `${year1VacancyMonths} Month Vacancy`, adjustedAnnualCashFlow: year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) - ((inputs.annualRent / 12) * year1VacancyMonths), adjustedMonthlyCashFlow: (year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) - ((inputs.annualRent / 12) * year1VacancyMonths)) / 12, cocAfterEvent: ((year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) - ((inputs.annualRent / 12) * year1VacancyMonths)) / Math.max(1, totalInitialInvestment)) * 100, remainingReserves: reserveCapital - ((inputs.annualRent / 12) * year1VacancyMonths), monthsToRecover: ((inputs.annualRent / 12) * year1VacancyMonths) / Math.max(1, year1Months.reduce((sum, row) => sum + row.endingCashFlow, 0) / 12), reserveCoverageRatio: reserveCapital / Math.max(1, (inputs.annualRent / 12) * year1VacancyMonths) }
  ];

  const worstCase = stressTests.reduce((prev, curr) => curr.adjustedAnnualCashFlow < prev.adjustedAnnualCashFlow ? curr : prev, stressTests[0]);
  let stabilityLabel: 'Stable' | 'Manageable' | 'Tight' | 'Fragile' = 'Stable';
  if (reserveCapital < Math.abs(worstCase.adjustedAnnualCashFlow) || worstCase.adjustedAnnualCashFlow < 0) stabilityLabel = 'Fragile';
  else if (reserveCapital < annualizedCapEx) stabilityLabel = 'Tight';
  else if (reserveCapital < annualizedCapEx * 2) stabilityLabel = 'Manageable';

  const annualTimeline = [
    ...historicalActuals
      .sort((a, b) => a.year - b.year)
      .map((actual) => buildActualTimelineRow(actual, historicalDebtServiceByYear)),
    ...proForma.map((year, index) => buildForecastTimelineRow(year, baseYear + index, index === 0 ? 'current' : 'forecast'))
  ];

  const annualReserveSnapshot = proForma.map((year, yearIndex) => ({
    year: year.year,
    endingReserveBalance: monthlyProjection[Math.min(monthlyProjection.length - 1, ((yearIndex + 1) * 12) - 1)]?.endingReserveBalance ?? 0,
    reserveRelease: year.reserveRelease
  }));

  return {
    monthlyProjection,
    proForma,
    annualTimeline,
    metrics: {
      year1CoC,
      irr,
      moic,
      loanAmount,
      totalInitialInvestment,
      totalInitialInvestmentBreakdown,
      reserveCapital,
      monthlyPayment,
      netSalesProceeds: projectedNetProceeds,
      equityBuiltFromPrincipal: cumulativePrincipalPaid,
      cumulativePrincipalPaid,
      year1InterestPaid,
      year1PrincipalPaid,
      monthlyCashFlow,
      monthlyTrueReturn,
      monthlyPrincipalPaydown,
      monthlyAppreciation,
      monthlyNoi,
      annualCashFlow,
      annualPrincipalPaydown,
      annualAppreciation,
      annualTrueReturn,
      annualNoi,
      annualOperatingCashFlowBeforeReserves,
      annualReserveFunding,
      annualRepairReserveFunding,
      annualTurnoverReserveFunding,
      annualOperatingExpenses,
      effectiveGrossIncome,
      riskScore: Math.max(0, Math.min(100, ((loanAmount / Math.max(1, inputs.purchasePrice)) * 55) + (inputs.vacancyRate * 1.5))),
      annualizedCapEx,
      riskAdjustedAnnualCashFlow,
      largestLikelyExpense,
      monthsOfExpenseCoverage,
      allocatedMonthlyDebtService: monthlyPayment,
      allocatedOriginalLoanAmount: debtSnapshot?.allocatedOriginalLoanAmount || 0,
      allocatedCurrentLoanBalance: debtSnapshot?.allocatedCurrentBalance || 0,
      allocatedInterestRate: debtSnapshot?.interestRate || 0,
      linkedDebtName: debtSnapshot?.debtName || null,
      allocationPercent: debtSnapshot?.allocationPercent || 0,
      allocationMethod: debtSnapshot?.allocationMethod || null,
      irrCashFlowSeries,
      irrDebug: {
        reserveCapital,
        includesReserveReleaseOnSale,
        selectedHoldPeriodTerminalCashFlow: selectedHoldYear?.totalCashFlow || 0,
        annualReserveSnapshot
      },
      reserveDebug: {
        propertyReserves: inputs.propertyReserves ?? 0,
        portfolioReservesAllocated: inputs.portfolioReservesAllocated ?? 0,
        reserveCapital,
        annualCapexReserve: inputs.annualCapexReserve ?? 0,
        reserveFundingMode: inputs.reserveFundingMode,
        includeReserveReleaseOnSale: includesReserveReleaseOnSale,
        totalInitialInvestmentBreakdown,
        annualReserveSnapshot
      },
      investmentDebug: {
        purchasePrice: inputs.purchasePrice,
        financedAmount,
        equityContribution: totalInitialInvestmentBreakdown.equityContribution,
        acquisitionCosts: inputs.acquisitionCosts ?? 0,
        lenderFees: inputs.lenderFees ?? 0,
        initialCapex: inputs.initialCapex ?? 0,
        propertyReserves: inputs.propertyReserves ?? 0,
        portfolioReservesAllocated: inputs.portfolioReservesAllocated ?? 0,
        totalInitialInvestment,
        reserveCapital,
        selectedYearReserveRelease: selectedHoldYear?.reserveRelease ?? 0
      },
      stabilityLabel,
      stressTests
    },
    debtSnapshot
  };
};
